import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { getContactPage } from '../services/api';
import SEO from '../components/SEO';

const Contact = () => {
  const [contactData, setContactData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadContactData = async () => {
      try {
        const data = await getContactPage();
        setContactData(data);
      } catch (error) {
        console.error('Failed to load contact data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadContactData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <p className="text-xl text-gray-600 dark:text-gray-400">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pt-24 pb-12">
      <SEO 
        title="Contact Us"
        description="Get in touch with Otter River Properties. Contact our team for property inquiries, rental applications, or general questions about property management in Lynchburg, VA."
        keywords="contact Otter River Properties, Lynchburg property management contact, rental inquiries Lynchburg, property management phone number"
      />
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            {contactData?.hero_title || 'Contact Us'}
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            {contactData?.hero_subtitle || 'Get in touch with our team'}
          </p>
        </div>

        {/* Page Description */}
        {contactData?.page_description && (
          <div className="max-w-3xl mx-auto mb-12">
            <p className="text-lg text-gray-700 dark:text-gray-300 text-center leading-relaxed">
              {contactData.page_description}
            </p>
          </div>
        )}

        {/* Contact Information Cards */}
        <div className="max-w-7xl mx-auto mb-12 px-4">
          <div className="flex flex-wrap justify-center gap-6">
            {/* Address */}
            {contactData?.office_address && (
              <Card className="dark:bg-gray-900 dark:border-gray-800 hover:shadow-lg transition-shadow w-full sm:w-64">
                <CardContent className="p-6">
                  <div className="flex flex-col items-center text-center">
                    <div className="w-12 h-12 bg-otter-green/10 rounded-full flex items-center justify-center mb-4">
                      <MapPin className="h-6 w-6 text-otter-green" />
                    </div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Address</h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm">
                      {contactData.office_address}
                      {contactData.office_city_state_zip && (
                        <>
                          <br />
                          {contactData.office_city_state_zip}
                        </>
                      )}
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Phone */}
            {contactData?.office_phone && (
              <Card className="dark:bg-gray-900 dark:border-gray-800 hover:shadow-lg transition-shadow w-full sm:w-64">
                <CardContent className="p-6">
                  <div className="flex flex-col items-center text-center">
                    <div className="w-12 h-12 bg-otter-green/10 rounded-full flex items-center justify-center mb-4">
                      <Phone className="h-6 w-6 text-otter-green" />
                    </div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Phone</h3>
                    <a 
                      href={`tel:${contactData.office_phone.replace(/[^0-9]/g, '')}`}
                      className="text-otter-green hover:text-otter-green-600 dark:hover:text-otter-green-400 text-sm transition-colors"
                    >
                      {contactData.office_phone}
                    </a>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Email */}
            {contactData?.office_email && (
              <Card className="dark:bg-gray-900 dark:border-gray-800 hover:shadow-lg transition-shadow w-full sm:w-64">
                <CardContent className="p-6">
                  <div className="flex flex-col items-center text-center">
                    <div className="w-12 h-12 bg-otter-green/10 rounded-full flex items-center justify-center mb-4">
                      <Mail className="h-6 w-6 text-otter-green" />
                    </div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Email</h3>
                    <a 
                      href={`mailto:${contactData.office_email}`}
                      className="text-otter-green hover:text-otter-green-600 dark:hover:text-otter-green-400 text-sm transition-colors break-all"
                    >
                      {contactData.office_email}
                    </a>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Office Hours */}
            {contactData?.office_hours && (
              <Card className="dark:bg-gray-900 dark:border-gray-800 hover:shadow-lg transition-shadow w-full sm:w-64">
                <CardContent className="p-6">
                  <div className="flex flex-col items-center text-center">
                    <div className="w-12 h-12 bg-otter-green/10 rounded-full flex items-center justify-center mb-4">
                      <Clock className="h-6 w-6 text-otter-green" />
                    </div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Office Hours</h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm whitespace-pre-line">
                      {contactData.office_hours}
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Map */}
        {contactData?.map_embed_url && (
          <Card className="dark:bg-gray-900 dark:border-gray-800">
            <CardContent className="p-0">
              <div className="w-full h-96">
                <iframe
                  src={contactData.map_embed_url}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Office Location"
                  className="rounded-lg"
                ></iframe>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Contact;
